import { createFileRoute, redirect } from '@tanstack/react-router'
import { useShellStore, type SelectedKind, type ShellView } from '@/stores/shellStore'
import { useIsMobile } from '@/hooks/useMediaQuery'
import { cn } from '@/lib/utils'
import { Topbar } from '@/components/whca/Topbar'
import { Rail } from '@/components/whca/Rail'
import { Composer } from '@/components/whca/Composer'
import { BroadcastThread } from '@/components/whca/BroadcastThread'
import { DeliveryView } from '@/components/whca/DeliveryView'
import { ChatThread } from '@/components/whca/ChatThread'
import { GroupRosterView } from '@/components/whca/GroupRosterView'
import { NewChatDialog } from '@/components/whca/NewChatDialog'
import { ManageTemplatesDialog } from '@/components/whca/ManageTemplatesDialog'
import { NumberPoolDialog } from '@/components/whca/NumberPoolDialog'
import { ManageDepartmentsDialog } from '@/components/whca/ManageDepartmentsDialog'

export const Route = createFileRoute('/')({
  component: AppShell,
  // Guard in beforeLoad (not loader): the root's beforeLoad has already awaited
  // session hydration by the time this runs, so the store reflects the real
  // auth state. In a loader this raced hydration and bounced authed users to
  // login on every refresh.
  beforeLoad: ({ context }) => {
    if (context.userStore.getState().User == null) {
      throw redirect({ to: '/authentication' })
    }
  },
})

function AppShell() {
  const isMobile = useIsMobile()
  const mobileView = useShellStore((s) => s.mobileView)
  const view = useShellStore((s) => s.view)
  const selectedId = useShellStore((s) => s.selectedId)
  const selectedKind = useShellStore((s) => s.selectedKind)

  const railVisible = !isMobile || mobileView === 'list'
  const mainVisible = !isMobile || mobileView === 'detail'

  return (
    <div className="flex h-full flex-col overflow-hidden">
      <Topbar />

      <div className="flex min-h-0 flex-1 overflow-hidden">
        {railVisible && (
          <div
            className={cn(
              'flex min-h-0 flex-col',
              isMobile ? 'w-full flex-1' : 'w-[320px] flex-none border-r border-border',
            )}
          >
            <Rail />
          </div>
        )}

        {mainVisible && (
          <div className="flex min-w-0 flex-1 flex-col bg-surface">
            <MainPane view={view} selectedId={selectedId} selectedKind={selectedKind} />
          </div>
        )}
      </div>

      <NewChatDialog />
      <ManageTemplatesDialog />
      <NumberPoolDialog />
      <ManageDepartmentsDialog />
    </div>
  )
}

function MainPane({
  view,
  selectedId,
  selectedKind,
}: {
  view: ShellView
  selectedId: string | null
  selectedKind: SelectedKind | null
}) {
  if (view === 'composer' || !selectedId) {
    return <Composer />
  }

  if (selectedKind === 'group') {
    // Keyed so switching groups resets the roster's own search and filters —
    // carrying "only missing numbers" across to a different group would be a
    // quiet lie about what you're looking at.
    return <GroupRosterView key={selectedId} groupId={selectedId} />
  }

  if (selectedKind === 'chat') {
    return <ChatThread chatId={selectedId} />
  }

  // Broadcast
  if (view === 'delivery') {
    return <DeliveryView broadcastId={selectedId} />
  }
  return <BroadcastThread broadcastId={selectedId} />
}
